/**
 * Campos mínimos de documentos clínicos Ecuador.
 *
 * Receta: Resolución ACESS-2023-0030 art. 5, Ley Orgánica de Salud arts. 7.g y 167,
 * Acuerdo Ministerial 00031-2020 / 00052-2022.
 * Historia odontológica: HCU formulario 033 (MSP / CONASA), Ley Orgánica del SNS
 * arts. 11.d y 77, Acuerdo Ministerial 0009-2017 (HCE).
 */

export interface RecetaAcess {
  numeroSecuencial: string;
  ciudad: string;
  fechaIso: string;
  establecimiento: string;
  vigencia: string;
  pacienteApellidosNombres: string;
  edad: string;
  diagnosticoCie: string;
  alergias: string;
  medicamentos: MedicamentoAcess[];
  prescriptorNombre: string;
  registroAcess: string;
  telefonoPrescriptor: string;
  indicaciones: string;
  signosAlarma: string;
  recomendacionesNoFarmacologicas: string;
  firmaPngBase64?: string;
}

export interface MedicamentoAcess {
  dci: string;
  formaFarmaceutica: string;
  concentracion: string;
  via: string;
  cantidadNumero: string;
  cantidadLetras: string;
  dosis: string;
  frecuencia: string;
  duracion: string;
}

export interface Historia033 {
  establecimiento: string;
  historiaNumero: string;
  fechaIso: string;
  apellidosNombres: string;
  cedula: string;
  sexo: string;
  fechaNacimiento: string;
  edad: string;
  residencia: string;
  telefono: string;
  ocupacion: string;
  motivoConsulta: string;
  enfermedadActual: string;
  antecedentesPersonales: string;
  antecedentesFamiliares: string;
  alergias: string;
  signosVitales: string;
  examenEstomatologico: string;
  odontogramaNotas: string;
  diagnosticos: { cie: string; descripcion: string; preDef: string }[];
  prescripciones: string;
  profesional: string;
  registroAcess: string;
  firmaPngBase64?: string;
}

export function formatDateEc(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export function emptyReceta(establecimiento: string, ciudad: string): RecetaAcess {
  return {
    numeroSecuencial: '',
    ciudad,
    fechaIso: new Date().toISOString(),
    establecimiento,
    vigencia: 'Consulta externa: 3 días (antimicrobianos: 3 días)',
    pacienteApellidosNombres: '',
    edad: '',
    diagnosticoCie: '',
    alergias: 'NINGUNA CONOCIDA / ',
    medicamentos: [
      {
        dci: '',
        formaFarmaceutica: '',
        concentracion: '',
        via: '',
        cantidadNumero: '',
        cantidadLetras: '',
        dosis: '',
        frecuencia: '',
        duracion: '',
      },
    ],
    prescriptorNombre: '',
    registroAcess: '',
    telefonoPrescriptor: '',
    indicaciones: '',
    signosAlarma: '',
    recomendacionesNoFarmacologicas: '',
  };
}

export function asReceta(raw: unknown, establecimiento: string, ciudad: string): RecetaAcess {
  const base = emptyReceta(establecimiento, ciudad);
  if (!raw || typeof raw !== 'object') return base;
  const row = raw as Partial<RecetaAcess>;
  return {
    ...base,
    ...row,
    establecimiento: row.establecimiento || establecimiento,
    ciudad: row.ciudad || ciudad,
    medicamentos: Array.isArray(row.medicamentos) && row.medicamentos.length ? row.medicamentos : base.medicamentos,
  };
}

export function asHistoria(raw: unknown, establecimiento: string): Historia033 {
  const base = emptyHistoria(establecimiento);
  if (!raw || typeof raw !== 'object') return base;
  const row = raw as Partial<Historia033>;
  return {
    ...base,
    ...row,
    establecimiento: row.establecimiento || establecimiento,
    diagnosticos: Array.isArray(row.diagnosticos) && row.diagnosticos.length ? row.diagnosticos : base.diagnosticos,
  };
}

export function emptyHistoria(establecimiento: string): Historia033 {
  return {
    establecimiento,
    historiaNumero: '',
    fechaIso: new Date().toISOString(),
    apellidosNombres: '',
    cedula: '',
    sexo: '',
    fechaNacimiento: '',
    edad: '',
    residencia: '',
    telefono: '',
    ocupacion: '',
    motivoConsulta: '',
    enfermedadActual: '',
    antecedentesPersonales: '',
    antecedentesFamiliares: '',
    alergias: '',
    signosVitales: 'TA ___  FC ___  FR ___  T° ___',
    examenEstomatologico: '',
    odontogramaNotas: '',
    diagnosticos: [
      { cie: '', descripcion: '', preDef: 'PRE' },
      { cie: '', descripcion: '', preDef: 'DEF' },
    ],
    prescripciones: '',
    profesional: '',
    registroAcess: '',
  };
}

const PERM = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const TEMP = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

function box(label: string, value: string): string {
  return `<div class="field"><label>${label}</label><div class="box">${escapeHtml(value)}</div></div>`;
}

export function recetaHtml(rx: RecetaAcess, logo: string): string {
  const meds = rx.medicamentos
    .map(
      (m, i) => `
      <p><strong>${i + 1}. ${escapeHtml(m.dci) || 'DCI (nombre genérico, sin abreviaturas)'}</strong></p>
      <div class="fields">
        ${box('Forma farmacéutica', m.formaFarmaceutica)}
        ${box('Concentración', m.concentracion)}
        ${box('Vía', m.via)}
        ${box('Cantidad (n.º y letras)', `${m.cantidadNumero} ${m.cantidadLetras}`.trim())}
        ${box('Dosis / frecuencia', `${m.dosis} ${m.frecuencia}`.trim())}
        ${box('Duración', m.duracion)}
      </div>`
    )
    .join('');
  return `
    <article class="print-sheet">
      <header class="print-head">
        <div style="display:flex;gap:8px;align-items:center">
          <img src="${logo}" alt="" />
          <div>
            <p class="print-title">${escapeHtml(rx.establecimiento)}</p>
            <p class="print-sub">Receta médica · medicamentos de uso y consumo humano</p>
          </div>
        </div>
        <div class="print-meta">
          <div>N.º ${escapeHtml(rx.numeroSecuencial) || '________'}</div>
          <div>${escapeHtml(rx.ciudad)} · ${formatDateEc(rx.fechaIso)}</div>
          <div>Vigencia: ${escapeHtml(rx.vigencia)}</div>
        </div>
      </header>
      <p class="legal">
        ACESS-2023-0030 art. 5 · LOS arts. 7.g y 167 (DCI en primer lugar, sin clave ni abreviaturas) ·
        AM 00031-2020 / 00052-2022. La receta debe concordar con la historia clínica (art. 33 y 61).
      </p>
      <h3>Usuario / paciente</h3>
      <div class="fields">
        ${box('Apellidos y nombres', rx.pacienteApellidosNombres)}
        ${box('Edad (si &lt;5 años: años y meses)', rx.edad)}
        ${box('Diagnóstico CIE vigente', rx.diagnosticoCie)}
        ${box('Antecedentes de alergias', rx.alergias)}
      </div>
      <div class="rx-split">
        <div class="rx-col">
          <h3>Prescripción (reverso farmacia)</h3>
          ${meds}
        </div>
        <div class="rx-col">
          <h3>Indicaciones (desprendible al paciente)</h3>
          ${box('Paciente', rx.pacienteApellidosNombres)}
          ${box('Fecha', formatDateEc(rx.fechaIso))}
          ${box('Indicaciones (DCI, dosis, vía, duración)', rx.indicaciones)}
          ${box('Signos de alarma', rx.signosAlarma)}
          ${box('Recomendaciones no farmacológicas', rx.recomendacionesNoFarmacologicas)}
          <p>Contacto del prescriptor: ${escapeHtml(rx.telefonoPrescriptor)}</p>
        </div>
      </div>
      <div class="sign">
        <div class="line">Firma y sello del prescriptor (no rúbrica)<br>
          ${rx.firmaPngBase64 ? `<img alt="firma" src="${rx.firmaPngBase64.startsWith('data:') ? rx.firmaPngBase64 : 'data:image/png;base64,' + rx.firmaPngBase64}" style="max-height:56px;display:block;margin:0 auto 4px" />` : ''}
          ${escapeHtml(rx.prescriptorNombre)}</div>
        <div class="line">Dispensación farmacia<br>DISPENSADA PARCIALMENTE / COMPLETAMENTE</div>
      </div>
    </article>`;
}

export function historiaHtml(h: Historia033, logo: string): string {
  const dx = h.diagnosticos
    .map((d) => `${box('CIE', d.cie)}${box('Descripción', d.descripcion)}${box('PRE / DEF', d.preDef)}`)
    .join('');
  return `
    <article class="print-sheet">
      <header class="print-head">
        <div style="display:flex;gap:8px;align-items:center">
          <img src="${logo}" alt="" />
          <div>
            <p class="print-title">${escapeHtml(h.establecimiento)}</p>
            <p class="print-sub">SNS-MSP/HCU-form.033 · Historia clínica única de odontología</p>
          </div>
        </div>
        <div class="print-meta">
          <div>N.º HC ${escapeHtml(h.historiaNumero) || '________'}</div>
          <div>${formatDateEc(h.fechaIso)}</div>
        </div>
      </header>
      <p class="legal">
        Documento técnico-legal y confidencial (LOS art. 7.f; LOSNS arts. 11.d y 77; AM 0009-2017).
        No alterar ni repintar el odontograma. Diagnósticos con CIE obligatoria.
      </p>
      <h3>1. Filiación</h3>
      <div class="fields">
        ${box('Apellidos y nombres', h.apellidosNombres)}
        ${box('Cédula / documento', h.cedula)}
        ${box('Sexo', h.sexo)}
        ${box('Fecha de nacimiento', h.fechaNacimiento)}
        ${box('Edad', h.edad)}
        ${box('Residencia', h.residencia)}
        ${box('Teléfono', h.telefono)}
        ${box('Ocupación', h.ocupacion)}
      </div>
      <h3>2. Motivo y enfermedad actual</h3>
      <div class="fields full">
        ${box('Motivo de consulta', h.motivoConsulta)}
        ${box('Enfermedad actual', h.enfermedadActual)}
      </div>
      <h3>3. Antecedentes</h3>
      <div class="fields">
        ${box('Personales', h.antecedentesPersonales)}
        ${box('Familiares', h.antecedentesFamiliares)}
        ${box('Alergias', h.alergias)}
        ${box('Signos vitales', h.signosVitales)}
      </div>
      <h3>4. Examen estomatológico</h3>
      <div class="fields full">${box('Hallazgos', h.examenEstomatologico)}</div>
      <h3>5. Odontograma (FDI)</h3>
      <div class="odontogram">${PERM.map((n) => `<span>${n}</span>`).join('')}</div>
      <div class="odontogram">${TEMP.map((n) => `<span>${n}</span>`).join('')}</div>
      <div class="fields full">${box('Notas / índices CPOD', h.odontogramaNotas)}</div>
      <h3>6. Diagnóstico (CIE) y prescripciones</h3>
      <div class="fields">${dx}</div>
      <div class="fields full">${box('Prescripciones (deben coincidir con la receta)', h.prescripciones)}</div>
      <div class="sign">
        <div class="line">Profesional (firma, no rúbrica)<br>
          ${h.firmaPngBase64 ? `<img alt="firma" src="${h.firmaPngBase64.startsWith('data:') ? h.firmaPngBase64 : 'data:image/png;base64,' + h.firmaPngBase64}" style="max-height:56px;display:block;margin:0 auto 4px" />` : ''}
          ${escapeHtml(h.profesional)}</div>
        <div class="line">Paciente / representante<br>Consentimiento informado (HCU 024)</div>
      </div>
    </article>`;
}

function escapeHtml(value: string): string {
  return String(value || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!));
}
